import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, euro, ymd, STATUS } from "@/lib/api";
import { StatusBadge } from "@/pages/Account";

const range = (view, ref) => {
  const d = new Date(ref);
  if (view === "day") return [d, d];
  if (view === "week") {
    const s = new Date(d);
    s.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return [s, e];
  }
  return [new Date(d.getFullYear(), d.getMonth(), 1), new Date(d.getFullYear(), d.getMonth() + 1, 0)];
};

const shift = (view, ref, dir) => {
  const d = new Date(ref);
  if (view === "day") d.setDate(d.getDate() + dir);
  else if (view === "week") d.setDate(d.getDate() + 7 * dir);
  else d.setMonth(d.getMonth() + dir);
  return d;
};

const Btn = ({ children, ...p }) => (
  <button {...p} className="h-9 px-3 text-[11px] tracking-[0.1em] uppercase border border-zinc-300 hover:border-[#111111] disabled:opacity-40 transition-colors">{children}</button>
);

const Row = ({ b, onAction, busy }) => (
  <li className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid={`admin-booking-${b.code}`}>
    <div className="md:col-span-2 font-mono text-sm">{b.start}–{b.end}<span className="block text-xs text-zinc-400">{b.date}</span></div>
    <div className="md:col-span-4">
      <p className="font-semibold">{b.name} <span className="font-mono text-xs text-zinc-400">{b.code}</span></p>
      <p className="text-xs text-zinc-500 flex flex-wrap gap-3 mt-1">
        <a href={`tel:${b.phone}`} className="flex items-center gap-1 hover:text-[#111111]"><Phone className="w-3 h-3" />{b.phone}</a>
        <a href={`mailto:${b.email}`} className="flex items-center gap-1 hover:text-[#111111]"><Mail className="w-3 h-3" />{b.email}</a>
      </p>
      <p className="text-xs text-zinc-400 mt-1">{b.user_id ? "Cliente registrado" : "Invitado (sin puntos)"}{b.email_status === "failed" ? " · correo no enviado" : ""}</p>
    </div>
    <div className="md:col-span-2 text-sm">{b.service_name}<span className="block font-mono">{euro(b.amount_due)}{b.discount > 0 && <span className="text-xs text-zinc-400"> (recompensa)</span>}</span></div>
    <div className="md:col-span-1 flex md:flex-col gap-1 items-start">
      <StatusBadge status={b.status} testid={`admin-booking-status-${b.code}`} />
      {b.paid && <span className="text-[10px] tracking-[0.15em] uppercase px-2 py-1 border border-[#111111]" data-testid={`admin-booking-paid-${b.code}`}>Pagada</span>}
      {b.points_awarded && <span className="text-[10px] text-zinc-500">+{b.points} pts</span>}
    </div>
    <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
      {b.status === "pending" && <Btn disabled={busy} onClick={() => onAction(b, "confirm")} data-testid={`admin-confirm-${b.code}`}>Confirmar</Btn>}
      {["pending", "confirmed"].includes(b.status) && <Btn disabled={busy} onClick={() => onAction(b, "complete")} data-testid={`admin-complete-${b.code}`}>Completada</Btn>}
      {b.status !== "cancelled" && !b.paid && <Btn disabled={busy} onClick={() => onAction(b, "pay")} data-testid={`admin-pay-${b.code}`}>Pagada</Btn>}
      {["pending", "confirmed"].includes(b.status) && <Btn disabled={busy} onClick={() => window.confirm("¿Cancelar esta cita?") && onAction(b, "cancel")} data-testid={`admin-cancel-${b.code}`}>Cancelar</Btn>}
    </div>
  </li>
);

export default function AdminBookings() {
  const api = useApi();
  const [view, setView] = useState("day");
  const [ref, setRef] = useState(new Date());
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [s, e] = useMemo(() => range(view, ref), [view, ref]);

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    setItems(null);
    api("get", `/admin/bookings?start=${ymd(s)}&end=${ymd(e)}`)
      .then((r) => { if (id === reqId.current) setItems(r); })
      .catch((x) => { if (id === reqId.current) { toast.error(errMsg(x)); setItems([]); } });
  }, [api, s, e]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (b, a) => {
    setBusy(true);
    try {
      const r = await api("patch", `/admin/bookings/${b.id}`, { action: a });
      setItems((xs) => xs.map((x) => (x.id === b.id ? { ...x, ...r } : x)));
      toast.success(r.points_awarded && !b.points_awarded ? `Actualizado · +${r.points} puntos al cliente` : "Actualizado");
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  const shown = (items || []).filter((b) => filter === "all" || b.status === filter);
  const active = (items || []).filter((b) => b.status !== "cancelled");
  const fmt = (d) => d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex gap-1">
          {[["day", "Día"], ["week", "Semana"], ["month", "Mes"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`admin-view-${k}`} className={`h-10 px-4 text-xs tracking-[0.15em] uppercase border ${view === k ? "bg-[#111111] text-white border-[#111111]" : "border-zinc-300"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setRef(shift(view, ref, -1))} className="w-10 h-10 border border-zinc-300 flex items-center justify-center" data-testid="admin-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm min-w-[200px] text-center" data-testid="admin-range-label">{view === "day" ? fmt(s) : `${fmt(s)} – ${fmt(e)}`}</span>
          <button onClick={() => setRef(shift(view, ref, 1))} className="w-10 h-10 border border-zinc-300 flex items-center justify-center" data-testid="admin-next"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setRef(new Date())} className="h-10 px-3 text-xs underline" data-testid="admin-today">Hoy</button>
        </div>
        <select value={filter} onChange={(x) => setFilter(x.target.value)} className="h-10 px-3 border border-zinc-300 text-sm bg-white" data-testid="admin-status-filter">
          <option value="all">Todos los estados</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <p className="text-sm text-zinc-500 mb-4" data-testid="admin-bookings-count">{active.length} {active.length === 1 ? "cita activa" : "citas activas"} · {euro(active.reduce((a, b) => a + b.amount_due, 0))} previstos</p>
      {items === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div> :
        shown.length === 0 ? <p className="py-10 text-zinc-400 text-sm" data-testid="admin-no-bookings">No hay reservas en este periodo.</p> :
          <ul className="divide-y divide-zinc-200 border-y border-zinc-200">{shown.map((b) => <Row key={b.id} b={b} onAction={action} busy={busy} />)}</ul>}
    </div>
  );
}
